import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UsersTable } from "@/components/features/UsersTable";
import { FilterChips } from "@/components/features/FilterChips";
import { endUsersApi } from "@/services/endUsersApi";
import { UserFilters } from "@/types";
import {
  Search,
  Users as UsersIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

export function Users() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<UserFilters>({
    kyc_status: [],
    deletion_status: [],
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["end-users", page, searchQuery, filters],
    queryFn: () =>
      endUsersApi.getEndUsers({
        page,
        limit: 20,
        search: searchQuery || undefined,
        filters,
      }),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const handleSearch = () => {
    setSearchQuery(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleFilterChange = (newFilters: UserFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UsersIcon className="h-8 w-8 text-gray-700" />
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Users</CardTitle>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-64"
              />
              <Button onClick={handleSearch} variant="outline">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
          {data && (
            <div className="text-sm text-gray-500 mt-2">
              Showing {data.users.length} of {data.totalcount} users
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <FilterChips
              filters={filters}
              counts={data?.statusCounts}
              onFilterChange={handleFilterChange}
              loading={isLoading}
              error={error}
            />
          </div>

          <UsersTable
            users={data?.users || []}
            loading={isLoading}
            showPagination={true}
          />

          {/* Pagination Controls */}
          {data && data.totalcount > 0 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {Math.ceil(data.totalcount / 20)}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={
                    page >= Math.ceil(data.totalcount / 20) || isLoading
                  }
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
